import { baseApi, storefrontPath } from './baseApi'
import type { CustomerProfile } from '../features/auth/authSlice'

/** What every successful credential call hands back (epic H1). */
export interface AuthSession {
  token: string
  /** Seconds. Null when the backend sets no Sanctum expiry. */
  expires_in: number | null
  customer: CustomerProfile
}

export interface SignupOtpSent {
  mobile: string
  expires_in: number
  /** Seconds before the server accepts another send for this number. */
  resend_in?: number
  /** Only present while the backend runs with `auth.fake_otp` — no SMS gateway yet. */
  otp?: string
}

/** Body of a successful forgot-password call. Unknown numbers get the same shape. */
export interface ResetOtpSent {
  resend_in?: number
  /** Only present while the backend runs with `auth.fake_otp`. */
  otp?: string
}

export interface SignupOtpVerified {
  signup_token: string
  mobile: string
  expires_in: number
}

export interface RegisterArgs {
  slug: string
  signupToken: string
  mobile: string
  firstName: string
  lastName?: string
  email?: string | null
  password: string
}

export interface ResetPasswordArgs {
  slug: string
  mobile: string
  otp: string
  password: string
}

export interface VerifyEmailArgs {
  slug: string
  id: string
  hash: string
  expires: string
  signature: string
}

export const authApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    /** `recaptchaToken` is only present once the form has seen repeated failures
     *  (see `loginAttempts.ts`); the backend verifies it and decides what a missing
     *  or low-scoring token is worth. */
    signIn: build.mutation<
      AuthSession,
      { slug: string; mobile: string; password: string; recaptchaToken?: string | null }
    >({
      query: ({ slug, mobile, password, recaptchaToken }) => ({
        url: storefrontPath(slug, '/auth/login'),
        method: 'POST',
        ...(recaptchaToken ? { headers: { 'X-Recaptcha-Token': recaptchaToken } } : {}),
        body: { mobile, password, ...(recaptchaToken ? { recaptcha_token: recaptchaToken } : {}) },
      }),
      // A fresh session must not read another customer's cached account data.
      invalidatesTags: ['Profile', 'Booking'],
    }),

    sendSignupOtp: build.mutation<SignupOtpSent, { slug: string; mobile: string }>({
      query: ({ slug, mobile }) => ({
        url: storefrontPath(slug, '/auth/otp/send'),
        method: 'POST',
        body: { mobile },
      }),
    }),

    verifySignupOtp: build.mutation<SignupOtpVerified, { slug: string; mobile: string; otp: string }>({
      query: ({ slug, mobile, otp }) => ({
        url: storefrontPath(slug, '/auth/otp/verify'),
        method: 'POST',
        body: { mobile, otp },
      }),
    }),

    register: build.mutation<AuthSession, RegisterArgs>({
      query: ({ slug, signupToken, mobile, firstName, lastName, email, password }) => ({
        url: storefrontPath(slug, '/auth/register'),
        method: 'POST',
        body: {
          signup_token: signupToken,
          mobile,
          first_name: firstName,
          last_name: lastName ?? '',
          email: email || null,
          password,
        },
      }),
      invalidatesTags: ['Profile', 'Booking'],
    }),

    /** Sends the reset OTP. Answers the same way whether or not the number is
     *  registered, so it cannot be used to enumerate customers. */
    forgotPassword: build.mutation<ResetOtpSent | undefined, { slug: string; mobile: string }>({
      query: ({ slug, mobile }) => ({
        url: storefrontPath(slug, '/auth/forgot-password'),
        method: 'POST',
        body: { mobile },
      }),
    }),

    /** OC-427: checks the reset code without using it up, so a wrong or expired code
     *  is stopped on the code step. Errors carry `code`: otp_invalid | otp_expired | otp_locked. */
    verifyResetOtp: build.mutation<void, { slug: string; mobile: string; otp: string }>({
      query: ({ slug, mobile, otp }) => ({
        url: storefrontPath(slug, '/auth/reset-password/verify'),
        method: 'POST',
        body: { mobile, otp },
      }),
    }),

    /** Sets the new password. The API checks the (already verified) code again. */
    resetPassword: build.mutation<void, ResetPasswordArgs>({
      query: ({ slug, mobile, otp, password }) => ({
        url: storefrontPath(slug, '/auth/reset-password'),
        method: 'POST',
        body: { mobile, otp, password, password_confirmation: password },
      }),
    }),

    /** OC-340: confirms the email from the sign-up mail. The link carries a signature
     *  for exactly this API path, so id, hash, expires and signature go back unchanged. */
    verifyEmail: build.mutation<void, VerifyEmailArgs>({
      query: ({ slug, id, hash, expires, signature }) => ({
        url: `${storefrontPath(slug, `/auth/email/verify/${encodeURIComponent(id)}/${encodeURIComponent(hash)}`)}?${new URLSearchParams({ expires, signature })}`,
        method: 'POST',
      }),
      invalidatesTags: ['Profile'],
    }),

    signOut: build.mutation<void, string>({
      query: (slug) => ({ url: storefrontPath(slug, '/auth/logout'), method: 'POST' }),
      invalidatesTags: ['Profile', 'Booking'],
    }),
  }),
})

export const {
  useSignInMutation,
  useSendSignupOtpMutation,
  useVerifySignupOtpMutation,
  useRegisterMutation,
  useForgotPasswordMutation,
  useVerifyResetOtpMutation,
  useResetPasswordMutation,
  useVerifyEmailMutation,
  useSignOutMutation,
} = authApi
